/**
 * @fileoverview Registration utility functions for Click&Pledge.
 * Provides functions to check registration status and form publication state using Redux.
 */

import { checkIfFormAlreadyPublished as checkFormPublished } from './reduxDataUtils';

/**
 * Check if a donation form is already published for the given meeting.
 * Uses Redux state instead of SimpleDB API.
 *
 * @param {string} meetingUUID - The meeting UUID to check
 * @param {Object} state - Redux state
 * @returns {Promise<boolean>} True if form is already published, false otherwise
 */
export const checkIfFormAlreadyPublished = async (meetingUUID, state) => {
    try {
        return checkFormPublished(state, meetingUUID);
    } catch (error) {
        // If check fails, assume form is not published
        return false;
    }
};

/**
 * Check if user is already registered and form is published for the meeting.
 * Determines whether to skip the publish step and go directly to the donation form.
 * Uses Redux state instead of SimpleDB API.
 *
 * @param {Object} meetingData - Meeting data object containing meetingUUID
 * @param {Object} userData - User data object with authentication information
 * @param {Object} state - Redux state
 * @returns {Promise<Object>} Registration status object with boolean flags
 * @returns {Promise<Object>} returns.isAlreadyRegistered - Whether user is registered
 * @returns {Promise<Object>} returns.shouldSkipPublish - Whether to skip publish step
 * @returns {Promise<Object>} returns.formExists - Whether form exists in database
 */
export const checkRegistrationStatus = async (meetingData, userData, state) => {
    try {
        const isPublished = await checkIfFormAlreadyPublished(meetingData.meetingUUID, state);

        return {
            isAlreadyRegistered: isPublished,
            shouldSkipPublish: isPublished,
            formExists: isPublished
        };
    } catch (error) {
        return {
            isAlreadyRegistered: false,
            shouldSkipPublish: false,
            formExists: false
        };
    }
};
